import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useSectionMotion, useHoverDireccional } from './motion';
import { useIdioma, type Traducir } from '../../i18n/idioma';

/* Igual que en Preguntas: claves, no texto. El precio y la URL no se
   traducen, así que se quedan aquí tal cual. */
const DOCUMENTOS = [
    { clave: 'lp.tienda.d1', precio: '39', nota: true,  url: 'https://www.razaperronaacademy.com/category/residenciales' },
    { clave: 'lp.tienda.d2', precio: '49', nota: false, url: 'https://www.razaperronaacademy.com/category/comerciales' },
    { clave: 'lp.tienda.d3', precio: '39', nota: false, url: 'https://www.razaperronaacademy.com/category/cartas-residenciales' },
    { clave: 'lp.tienda.d4', precio: '49', nota: false, url: 'https://www.razaperronaacademy.com/category/cartas-comerciales' },
] as const;

/**
 * Tienda de documentos.
 *
 * Es una oferta secundaria y se compone como tal: un fold CORTO, una lista de
 * precios con filetes, sin rejilla de cuatro tarjetas ni cuatro identidades de
 * color. «El más vendido» es una nota de texto, no una píldora de color
 * flotando sobre la esquina de una tarjeta.
 */
const Tienda: React.FC = () => {
    const { t } = useIdioma();
    // Mismo barrido direccional que Academy, sobre la lista de documentos.
    const filas = useHoverDireccional<HTMLUListElement>();

    const scope = useSectionMotion<HTMLElement>(({ scope, reveal }) => {
        reveal('[data-fade]', { start: 'top 74%' });
        reveal('[data-fila]', {
            trigger: scope.querySelector('[data-lista]'),
            start: 'top 82%',
            y: 18,
            duration: 0.6,
            stagger: 0.08,
        });
    });

    return (
        <section
            ref={scope}
            id="tienda"
            /* EL FOLD AMARILLO. Decisión del cliente, agosto 2026, y deliberada
               —que es la condición que pedía Producto.tsx antes de recuperar el
               baño de color.

               Va AQUÍ y no en Producto por lo que enseñó aquel fracaso: lo que
               convirtió el amarillo en «un grito» no fue el color, fue el
               VOLUMEN. Producto es un fold largo lleno de maquetas navy de la
               app, y el amarillo peleaba con cada una. La Tienda es corto, es
               una lista de filetes y su tipografía ya era navy: el amarillo
               aquí es superficie, que es como manda usarlo el principio 3 del
               PRODUCT.md, sin competir con nada.

               `rp-on-yellow` no es decorativo: reprograma el anillo de foco,
               que por defecto es de este mismo amarillo y sobre este fondo
               sería invisible. Ver landing.css. */
            className="rp-on-yellow bg-brand-400 px-5 py-[clamp(4rem,9vw,7rem)] sm:px-8"
        >
            <div className="mx-auto grid max-w-[1240px] gap-x-16 gap-y-10 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <h2 data-fade className="rp-h3 text-navy-950">
                        {t('lp.tienda.titular')}
                    </h2>
                    {/* Navy translúcido, NO acero. El acero es un gris entintado
                        de navy y sobre amarillo se enloda —el gris sobre color
                        siempre se ve deslavado—. Un navy al 75 % da 6.5:1 y
                        además mantiene la jerarquía con el titular, que va
                        opaco. */}
                    <p data-fade className="rp-body mt-4 text-navy-950/75">
                        {t('lp.tienda.lead')}
                    </p>
                    <a
                        data-fade
                        href="https://www.razaperronaacademy.com/category/all-products"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rp-link mt-7 inline-flex text-navy-950"
                    >
                        {t('lp.tienda.enlace')}
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </a>
                </div>

                {/* Filetes en navy translúcido: `acero-200` es casi blanco y
                    sobre amarillo desaparecía, dejando la lista sin estructura. */}
                <ul ref={filas} data-lista className="border-b border-navy-950/20 lg:col-span-7 lg:col-start-6">
                    {DOCUMENTOS.map((d) => (
                        /* Filete en el <li> (recto y de lado a lado) y fondo del
                           hover en el <a> (redondeado). Ver la nota de Academy. */
                        <li key={d.clave} data-fila className="border-t border-navy-950/20">
                            <a
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative isolate -mx-4 flex items-center justify-between gap-6 overflow-hidden rounded-xl px-4 py-5"
                            >
                                {/* Ver la nota de Academy: fundido por CSS de base,
                                    barrido direccional cuando hay ratón. */}
                                <span
                                    data-superficie
                                    aria-hidden="true"
                                    className="absolute inset-0 -z-10 bg-navy-950/[0.07] opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
                                />
                                <span className="min-w-0">
                                    <span className="flex flex-wrap items-baseline gap-x-3">
                                        <span className="rp-fila-titulo text-[17px] tracking-tight text-navy-950">
                                            {t(`${d.clave}.nombre` as Parameters<Traducir>[0])}
                                        </span>
                                        {/* El punto era AMARILLO, y con razón: sobre
                                            blanco el amarillo no puede ser texto
                                            (1.6:1), así que el color de marca entraba
                                            como punto. Al volverse amarilla la
                                            superficie, ese razonamiento se invierte —un
                                            punto amarillo sobre amarillo no existe— y
                                            el punto pasa a navy, que es lo que ahora
                                            contrasta (12.4:1). */}
                                        {d.nota && (
                                            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-navy-950/75">
                                                <span
                                                    className="h-1.5 w-1.5 rounded-full bg-navy-950"
                                                    aria-hidden="true"
                                                />
                                                {t('lp.tienda.d1.nota')}
                                            </span>
                                        )}
                                    </span>
                                    <span className="mt-1 block text-[14.5px] leading-relaxed text-navy-950/75">
                                        {t(`${d.clave}.texto` as Parameters<Traducir>[0])}
                                    </span>
                                </span>
                                <span className="flex shrink-0 items-center gap-3">
                                    <span className="text-[22px] font-bold tracking-tight text-navy-950">
                                        <span className="text-[15px] align-top">$</span>
                                        {d.precio}
                                    </span>
                                    <ArrowUpRight
                                        aria-hidden="true"
                                        className="h-[18px] w-[18px] text-navy-950/45 transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-navy-950"
                                    />
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default Tienda;
